import { use } from 'react';
import type { FileRecord, Pagination } from "@/common/define";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { message, type UploadFile } from "antd";
import { saveAs } from "file-saver";
import { concat, forkJoin, from, of } from "rxjs";
import {
  catchError,
  concatMap,
  delay,
  filter,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import type { ColumnConfig } from "../../pages/DrawingsPage/TableConfiguration";
import { fileColumns } from "../../pages/FilePage/filesTableColums";
import { FolderService } from "../../services/FolderService"; // Có thể đổi lại thành FileService nếu tách riêng
import { startLoading, stopLoading } from "../loading";
import type { RootEpic } from "../types";
// import { uploadActions } from "../upload/uploadSlice";
import { FileService } from "@/services/FileService";
import { iraiActions } from "@/store/irai";
import filesService, {
  type FilterFilesRequest,
  type FileVersionDto,
  type UserStorageInfo,
} from "../../pages/FilePage/services/files.service";
import { mapApiToFolderRecord } from "../../pages/FilePage/utils/fileUtils";
import { fetchCacheInfo } from "../../pages/FilePage/helpers/fileHelper";
import { getUserProfile } from "@/utils/jwtHelper";
import {
  fileStatusActions,
  selectMergedFileStatus,
} from "../convertFile/convertFile.slice";
import { createSelector } from "@reduxjs/toolkit";
import { uploadFileActions } from "../uploadFiles/uploadFileSlice";
import {
  fetchFilesByFolderIdEpic,
  fetchStorageForLabelsEpic,
} from "./file.epics";
import i18next from "i18next";
// Interface giữ nguyên vì còn dùng cho label logic
export interface LabelChild {
  idChildren: string;
  type: string;
}

export interface LabelData {
  name: string;
  color: string;
  type: string;
  labelCode: string;
  children: LabelChild[];
  parentId: string | null;
}

interface FileItem {
  idChildren: string;
  type: string;
}

interface FileData {
  fileId: string;
  cloudLink: string;
  toLowerCase(): unknown;
  id: string;
  guid_file: string;
  name: string;
  color: string;
  createdDate: string;
  updatedAt: string;
  type: string;
  labelCode: string;
  children: FileItem[];
  parentId?: string;
  createUser?: string;
  clientId?: string;
  size?: string | "0 kb";
}

// Search files state
export interface SearchFilesState {
  results: FileRecord[];
  totalCount?: number;
}

interface FileState {
  projectID: number;
  loading: boolean;
  visibleSideBar: boolean;
  files: FileItem[];
  parentFiles: FileData[];
  currentFolderFiles: FileRecord[]; // Files for current folder (from epic)
  error: string | null;
  uploadFileUid: string;
  visibleAddFile: boolean;
  labelId: string;
  isLoadingProgress: boolean;
  analyticGuidMap: { [fileId: string]: string };
  fileHistory: string[];
  pagination: Pagination;
  openPanels: FileRecord[];
  columns: ColumnConfig[];
  folderRootId: string;
  fileIds: string[];
  searchFiles: SearchFilesState;
  fileVersions: FileVersionDto[];
  userStorage: UserStorageInfo | null;
  userStorageLoading: boolean;
  userCapacitySize: number | null; // Capacity size của user storage
  thumbnailsById: { [documentId: string]: string }; // map documentId -> thumbnail URL
  thumbnailsLoading: boolean;
}

export interface FileContentParams {
  folderRootId: string;
  paging?: boolean;
  page: number;
  pageSize: number;
}

const savedColumns = localStorage.getItem("filesTableConfig");

// Helper functions for sessionStorage
const getFilePaginationFromSession = () => {
  try {
    const savedPagination = sessionStorage.getItem('filePagination');
    if (savedPagination) {
      return JSON.parse(savedPagination);
    }
  } catch (error) {
    console.error('Failed to parse sessionStorage filePagination:', error);
  }
  return null;
};

const saveFilePaginationToSession = (pagination: Pagination) => {
  try {
    sessionStorage.setItem('filePagination', JSON.stringify(pagination));
  } catch (error) {
    console.error('Failed to save filePagination to sessionStorage:', error);
  }
};

const sessionPagination = getFilePaginationFromSession();

const initialState: FileState = {
  loading: false,
  visibleSideBar: false,
  files: [],
  parentFiles: [],
  currentFolderFiles: [], // Initialize new field
  error: null,
  uploadFileUid: "",
  visibleAddFile: false,
  labelId: "",
  isLoadingProgress: false,
  analyticGuidMap: {},
  fileHistory: [],
  projectID: 1,
  pagination: sessionPagination || {
    page: 1,
    pageCount: 0,
    pageSize: 20,
    queryCount: 0,
    firstRowIndex: 0,
    lastRowIndex: 0,
  },
  openPanels: [],
  columns: savedColumns ? JSON.parse(savedColumns) : fileColumns,
  folderRootId: "",
  fileIds: [],
  searchFiles: {
    results: [],
  },
  fileVersions: [],
  userStorage: null,
  userStorageLoading: false,
  userCapacitySize: null,
  thumbnailsById: {},
  thumbnailsLoading: false,
};

const fileSlice = createSlice({
  name: "file",
  initialState,
  reducers: {
    // New epic-based actions for fetching files
    fetchFilesRequest(
      state,
      _action: PayloadAction<{
        folderId: string;
        page: number;
        pageSize: number;
      }>
    ) {
      state.loading = true;
      state.error = null;
    },
    fetchFilesIntermediate(
      state,
      action: PayloadAction<{
        files: FileRecord[];
        folderId: string;
        pagination: any;
      }>
    ) {
      // Intermediate state - files fetched but storage not yet loaded
      state.currentFolderFiles = action.payload.files;
      state.pagination = action.payload.pagination;
    },
    fetchFilesSuccess(
      state,
      action: PayloadAction<{
        files: any[];
        folderId: string;
        pagination: any;
      }>
    ) {
      state.loading = false;
      state.parentFiles = action.payload.files;
      state.pagination = action.payload.pagination;
      state.error = null;
    },
    fetchFilesFailure(state, action: PayloadAction<{ error: string }>) {
      state.loading = false;
      state.error = action.payload.error;
      state.currentFolderFiles = [];
    },

    searchFilesRequest(
      state,
      _action: PayloadAction<{
        companyId: number;
        projectId: string;
        contentKey?: string;
        fileNameKey?: string;
      }>
    ) {
      state.searchFiles = { results: [] };
      state.loading = true;
      state.error = null;
    },
    searchFilesSuccess(
      state,
      action: PayloadAction<{ files: FileRecord[]; totalCount?: number }>
    ) {
      state.searchFiles = {
        results: action.payload.files || [],
        totalCount: action.payload.totalCount || 0,
      };
      state.loading = false;
    },
    searchFilesFailure(state, action: PayloadAction<string>) {
      state.searchFiles = {
        results: [],
      };
      state.loading = false;
    },
    getFilesRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    getFilesSuccess: (state, action: PayloadAction<FileItem[]>) => {
      state.loading = false;
      state.files = action.payload;
    },
    getFilesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    createFileRequest: (
      state,
      _action: PayloadAction<{ projectId: string; data?: LabelData }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    createFileSuccess: (state, action: PayloadAction<FileItem>) => {
      state.loading = false;
      state.files.push(action.payload);
    },
    createFileFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    getFilesByProjectId: (state, _action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },

    setFiles: (state, action: PayloadAction<FileItem[]>) => {
      state.files = action.payload;
    },
    setParentFiles: (state, action: PayloadAction<any[]>) => {
      state.parentFiles = action.payload;
      state.loading = false;
    },
    setPagination: (state, action: PayloadAction<Partial<Pagination> | any>) => {
      const payload = action.payload || {};
      const page = payload.page ?? state.pagination.page ?? 1;
      const pageSize = payload.pageSize ?? state.pagination.pageSize ?? 20;
      const pageCount = payload.pageCount ?? state.pagination.pageCount ?? 0;
      const queryCount = payload.queryCount ?? state.pagination.queryCount ?? 0;
      const firstRowIndex =
        payload.firstRowIndex ?? (queryCount === 0 ? 0 : (page - 1) * pageSize + 1);
      const lastRowIndex =
        payload.lastRowIndex ?? (queryCount === 0 ? 0 : Math.min(page * pageSize, queryCount));

      state.pagination = {
        page,
        pageCount,
        pageSize,
        queryCount,
        firstRowIndex,
        lastRowIndex,
      };
      
      // Save to sessionStorage
      saveFilePaginationToSession(state.pagination);
    },

    getAllFileContent: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    getAllFileContentPaged: (
      state,
      action: PayloadAction<FileContentParams>
    ) => {
      state.loading = true;
      state.error = null;
    },

    uploadFile: (
      state,
      action: PayloadAction<{
        fileList: UploadFile[];
        companyId: string;
        labelId: string;
        isPublish: boolean;
      }>
    ) => {
      state.loading = true;
    },

    setUploadFileUid: (state, action: PayloadAction<string>) => {
      state.uploadFileUid = action.payload;
    },

    setVisibleAddFile: (state, action: PayloadAction<boolean>) => {
      state.visibleAddFile = action.payload;
    },

    setLabelId: (state, action: PayloadAction<string>) => {
      state.labelId = action.payload;
    },

    setIsLoadingProgress: (state, action: PayloadAction<boolean>) => {
      state.isLoadingProgress = action.payload;
    },

    deleteFile: (
      state,
      action: PayloadAction<{ fileId?: string; projectId?: string }>
    ) => {
      state.isLoadingProgress = true;
    },

    deleteFileGroup: (
      state,
      action: PayloadAction<{ folderId?: string; projectId?: string }>
    ) => {
      state.isLoadingProgress = true;
    },

    setAnalyticGuidForFile: (
      state,
      action: PayloadAction<{ fileId: string; guid: string }>
    ) => {
      state.analyticGuidMap[action.payload.fileId] = action.payload.guid;
    },
    clearAnalyticGuidForFile: (
      state,
      action: PayloadAction<{ fileId: string }>
    ) => {
      delete state.analyticGuidMap[action.payload.fileId];
    },
    clearAllAnalyticGuid: (state) => {
      state.analyticGuidMap = {};
    },
    pushFileHistory: (state, action: PayloadAction<string>) => {
      if (!state.fileHistory) {
        state.fileHistory = [];
        state.projectID = 1;
      }
      state.fileHistory.push(action.payload);
    },
    setColumns: (state, action: PayloadAction<ColumnConfig[]>) => {
      state.columns = action.payload;
    },
    openInfoPanels: (state, action: PayloadAction<FileRecord[]>) => {
      const newPanels = action.payload.filter(
        (newPanel) =>
          !state.openPanels.some((panel) => panel.id === newPanel.id)
      );
      state.openPanels = [...state.openPanels, ...newPanels];
    },
    closeInfoPanel: (state, action: PayloadAction<string>) => {
      state.openPanels = state.openPanels.filter(
        (panel) => panel.id !== action.payload
      );
    },
    closeAllInfoPanels: (state) => {
      state.openPanels = [];
    },

    popFileHistory: (state) => {
      state.fileHistory.pop();
    },
    clearFileHistory: (state) => {
      state.fileHistory = [];
    },
    backToPreviousFolder: (state) => {},
    resetFileState: () => initialState,
    setVisibleSidebar: (state, action: PayloadAction<boolean>) => {
      state.visibleSideBar = action.payload;
    },
    getDocumentByIdRequest: (
      state,
      action: PayloadAction<{ id: string; file: UploadFile }>
    ) => {
      console.log("getDocumentByIdRequest action", action.payload);
    },
    downloadFile: (state, action) => {},
    fetchFile: (state, action: PayloadAction<{ fileId: string }>) => {
      console.log("Fetching file with ID:", action.payload.fileId);
    },

    filterFilesRequest: (
      state,
      _action: PayloadAction<{
        filterRequest: FilterFilesRequest;
        page: number;
        pageSize: number;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    filterFilesSuccess: (state, action: PayloadAction<FileData[]>) => {
      state.loading = false;
    },
    filterFilesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setFileIds(state, action: PayloadAction<string[]>) {
      state.fileIds = action.payload;
    },
    getFileVersionsRequest: (state, _action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    getFileVersionsSuccess: (
      state,
      action: PayloadAction<FileVersionDto[]>
    ) => {
      state.loading = false;
      state.fileVersions = action.payload;
    },
    getFileVersionsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.fileVersions = [];
    },
    setDocumentActiveRequest: (
      state,
      _action: PayloadAction<{
        newDocumentId: string;
        oldDocumentId?: string;
        fileId: string;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    setDocumentActiveSuccess: (
      state,
      action: PayloadAction<{ newDocumentId: string; fileId: string }>
    ) => {
      state.loading = false;
      // Cập nhật fileVersions: set current cho document được chọn
      state.fileVersions = state.fileVersions.map((version) =>
        version.id === action.payload.newDocumentId
          ? { ...version, isActive: true }
          : { ...version, isActive: false }
      );
    },
    setDocumentActiveFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    getUserStorageInfoRequest: (state) => {
      state.userStorageLoading = true;
      state.error = null;
    },
    getUserStorageInfoSuccess: (
      state,
      action: PayloadAction<UserStorageInfo>
    ) => {
      state.userStorageLoading = false;
      state.userStorage = action.payload;
      state.userCapacitySize = action.payload.capacitySize ?? null;
    },
    getUserStorageInfoFailure: (state, action: PayloadAction<string>) => {
      state.userStorageLoading = false;
      state.error = action.payload;
    },

    updateUserStorageSize: (state, action: PayloadAction<number>) => {
      if (!state.userStorage) return;

      const currentSize = Number(state.userStorage.storageSize ?? 0);
      const delta = Number(action.payload ?? 0);
      state.userStorage.storageSize = currentSize + delta;
    },

    // Thumbnails: fetch preview URLs for files
    fetchThumbnailsRequest: (
      state,
      _action: PayloadAction<{
        items: Array<{ id: string; cloudLink?: string; bucketName?: string }>;
      }>
    ) => {
      state.thumbnailsLoading = true;
      state.error = null;
    },
    fetchThumbnailsSuccess: (
      state,
      action: PayloadAction<{ [documentId: string]: string }>
    ) => {
      state.thumbnailsLoading = false;
      // merge into map
      state.thumbnailsById = { ...state.thumbnailsById, ...action.payload };
    },
    fetchThumbnailsFailure: (state, action: PayloadAction<string>) => {
      state.thumbnailsLoading = false;
      state.error = action.payload;
    },
  },
});

// Fetch thumbnails epic
export const fetchThumbnailsEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(fileActions.fetchThumbnailsRequest.match),
    switchMap((action) => {
      const items = action.payload.items || [];
      if (!items.length) {
        return of(fileActions.fetchThumbnailsSuccess({}));
      }
      // Call viewPDF for each item to get preview URL
      const requests = items.map((it) =>
        filesService
          .viewPDF(it.cloudLink || "", it.bucketName || "", it.id)
          .pipe(
            map((res) => ({ id: it.id, url: res?.url || "" })),
            catchError(() => of({ id: it.id, url: "" }))
          )
      );
      return forkJoin(requests).pipe(
        map((results) => {
          const mapResult: { [id: string]: string } = {};
          results.forEach((r) => {
            if (r.id) mapResult[r.id] = r.url || "";
          });
          return fileActions.fetchThumbnailsSuccess(mapResult);
        }),
        catchError((err) => of(fileActions.fetchThumbnailsFailure(String(err))))
      );
    })
  );

// === Epics ===
export const getFilesEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(fileActions.getFilesByProjectId.match),
    switchMap((action) =>
      FolderService.Get.getFolderByProjectId(action.payload).pipe(
        switchMap((res) => {
          return from([fileActions.setParentFiles(res.results || [])]);
        }),
        catchError((err) => {
          console.error(err.message || "Error loading files");
          return of(
            fileActions.getFilesFailure(err.message || "Error loading files")
          );
        })
      )
    )
  );

export const getAllFileContent$: RootEpic = (action$) =>
  action$.pipe(
    filter(fileActions.getAllFileContentPaged.match),
    switchMap((action) => {
      const { folderRootId, page, pageSize } = action.payload;
      const query = `?page=${page}&pageSize=${pageSize}`;
      return concat(
        FolderService.Get.getFilesByFolderId(folderRootId + query).pipe(
          switchMap((res) => {
            return from([
              fileActions.setParentFiles(res.data.results),
              fileActions.setPagination(res.data),
            ]);
          }),
          catchError((err) => {
            console.error(err.message || "Error loading files");
            return of(
              fileActions.getFilesFailure(err.message || "Error loading files")
            );
          })
        ),
      );
    })
  );

export const downloadFile$: RootEpic = (action$, state$) => {
  return action$.pipe(
    filter(fileActions.downloadFile.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const { document, search } = action.payload;
      return concat(
        [startLoading({ key: "DownloadingDocument" })],
        FolderService.Get.downloadFileById(document.id, { search }).pipe(
          mergeMap((documentBlob: any) => {
            if (documentBlob) {
              saveAs(documentBlob, document.name);
            }
            return [];
          }),
          catchError((err) => {
            console.error(err.message || "Error downloading file");
            return of(
              fileActions.createFileFailure(
                err.message || "Error downloading file"
              )
            );
          })
        ),
        [stopLoading({ key: "DownloadingDocument" })]
      );
    })
  );
};

export const createFileEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(fileActions.createFileRequest.match),
    switchMap((action) => {
      const { projectId, data } = action.payload;
      if (!data) {
        console.error("File data is required");
        return of(fileActions.createFileFailure("File data is required"));
      }

      return concat(
        [startLoading({ key: "createFile" })],
        FolderService.Post.createNewFolder(data, projectId).pipe(
          map(() => fileActions.getFilesByProjectId(projectId ?? "")),
          catchError((err) => {
            console.error(err.message || "Error creating file");
            return of(
              fileActions.createFileFailure(
                err.message || "Error creating file"
              )
            );
          })
        ),
        [
          fileActions.setVisibleAddFile(false),
          stopLoading({ key: "createFile" }),
        ]
      );
    })
  );

export const deleteFileEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(fileActions.deleteFile.match),
    switchMap((action) => {
      const { fileId, projectId } = action.payload;
      if (!fileId) {
        console.error("File ID is required");
        return of(fileActions.createFileFailure("File ID is required"));
      }

      return concat(
        [startLoading({ key: "deleteFile" })],
        FolderService.Delete.deleteFileById(fileId).pipe(
          map(() => fileActions.getAllFileContent(projectId || "")),
          catchError((err) => {
            console.error(err.message || "Error deleting file");
            return of(
              fileActions.createFileFailure(
                err.message || "Error deleting file"
              )
            );
          })
        ),
        [stopLoading({ key: "deleteFile" })]
      );
    })
  );

export const deleteFileGroupEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(fileActions.deleteFileGroup.match),
    switchMap((action) => {
      const { folderId, projectId } = action.payload;
      if (!folderId) {
        console.error("Group ID is required");
        return of(fileActions.createFileFailure("Group ID is required"));
      }

      return concat(
        [startLoading({ key: "deleteFileGroup" })],
        FolderService.Delete.deleteFolderById(folderId).pipe(
          map(() => fileActions.getAllFileContent(projectId || "")),
          catchError((err) => {
            console.error(err.message || "Error deleting file group");
            return of(
              fileActions.createFileFailure(
                err.message || "Error deleting file group"
              )
            );
          })
        ),
        [stopLoading({ key: "deleteFileGroup" })]
      );
    })
  );

export const backToPreviousFolderEpic$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(fileActions.backToPreviousFolder.match),
    switchMap(() => {
      const history = state$.value.file.fileHistory;
      const prevFolderId = history[history.length - 2];
      if (prevFolderId) {
        return of(fileActions.getAllFileContent(prevFolderId));
      }

      return of(fileActions.getFilesByProjectId(state$.value.file.projectID));
    })
  );

export const fetchIraiId$: RootEpic = (action$) => {
  return action$.pipe(
    filter(fileActions.fetchFile.match),
    switchMap((action) => {
      console.log("Epic triggered with fileId:", action.payload.fileId);

      const { fileId } = action.payload;

      return from(FileService.Get.getIraiByGuidFile(fileId)).pipe(
        map((response) => {
          console.log("API response:", response);

          const iraiId = response?.irai_id ?? response?.data?.irai_id;

          if (!iraiId) {
            // ❌ KHÔNG throw Error
            // ✅ Thay bằng action báo lỗi
            return {
              type: "error/message",
              payload: "IRAI ID not found in response",
            };
          }

          return iraiActions.setIraiId(iraiId);
        }),
        catchError((error) => {
          console.error("Error fetching IRAI ID:", error);
          return of({
            type: "error/message",
            payload: "Failed to fetch IRAI ID",
          });
        })
      );
    })
  );
};

export const getDocumentByIdRequest$: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(fileActions.getDocumentByIdRequest.match),
    concatMap((action) => {
      // Đổi switchMap thành concatMap để tuần tự
      const { id, file } = action.payload;
      return concat(
        of(startLoading({ key: "getDocumentById" })),
        FolderService.Get.getDocumentById(id).pipe(
          map((res) => {
            // Chỉ cập nhật uploadResponse với đầy đủ thông tin
            // autoExtractEpic$ sẽ tự động xử lý extract khi có cloudLink
            return uploadActions.setUploadResponse({
              uid: file.uid,
              response: {
                success: true,
                fileId: res.fileId,
                cloudLink: res.cloudLink,
                id: id,
              },
            });
          }),
          catchError((err) => {
            console.error(err.message || "Error getting File data");
            return of(
              uploadActions.extractFileFailure(
                err.message || "Error getting File data"
              )
            );
          }),
          delay(750) // Thêm delay sau khi xử lý xong mỗi lần gọi API
        ),
        of(stopLoading({ key: "getDocumentById" }))
      );
    })
  );

export const filterFilesEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(fileActions.filterFilesRequest.match),
    switchMap((action) => {
      const { filterRequest, page, pageSize } = action.payload;

      return concat(
        [startLoading({ key: "filterFiles" })],

        from(
          filesService
            .getFilteredFiles(filterRequest, page, pageSize)
            .toPromise()
        ).pipe(
          switchMap((projectRes) => {
            if (!projectRes) {
              return of(fileActions.filterFilesFailure("No project response"));
            }

            const projectFiles = projectRes.files;

            const ids = projectFiles.map((f) => String(f.id));
            const fileStatusActions$ = ids.flatMap((id) => [
              fileStatusActions.getTaskByBaseFileId({ baseFileId: id }),
              fileStatusActions.fetchLogsStart({ fileId: id }),
            ]);
            return from([
              fileActions.setFileIds(ids), // TẤT CẢ files, không chỉ RVT
              fileActions.filterFilesSuccess(projectFiles as any),
              ...fileStatusActions$,
            ]);
          }),
          catchError((err) => {
            console.error("[filterFilesEpic] Error:", err);
            console.error(err.message || "Error filtering files");
            return of(
              fileActions.filterFilesFailure(
                err.message || "Error filtering files"
              )
            );
          })
        ),

        [stopLoading({ key: "filterFiles" })]
      );
    })
  );

export const SearchFilesEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(fileActions.searchFilesRequest.match),
    switchMap((action) => {
      // action.payload có thể là object chứa projectId, content, fileName
      const request = action.payload;
      // Hỗ trợ cả dạng string cũ và object mới
      let companyId = "";
      let projectId = "";
      let contentKey: string | undefined = undefined;
      let fileNameKey: string | undefined = undefined;
      if (typeof request === "object" && request !== null) {
        companyId = request.companyId;
        projectId = request.projectId;
        contentKey = request.contentKey;
        fileNameKey = request.fileNameKey;
      } else {
        projectId = request;
      }
      console.log("Searching files with:", {
        companyId,
        projectId,
        contentKey,
        fileNameKey,
      });
      return concat(
        [startLoading({ key: "searchFiles" })],
        from(
          filesService
            .getSearchFiles(companyId, projectId, contentKey, fileNameKey)
            .toPromise()
        ).pipe(
          switchMap((projectRes) => {

            if (!projectRes || !Array.isArray(projectRes?.results)) {
              return of(
                fileActions.searchFilesFailure("No project files returned")
              );
            }

            const projectFiles = projectRes.results;

            const ids = projectFiles.map((f) => String(f.id));

            return from([
              fileActions.setFileIds(ids),
              fileActions.searchFilesSuccess({
                files: projectFiles as any,
                totalCount: projectRes.queryCount || projectFiles.length,
              }),
            ]);
          }),
          catchError((err) => {
            console.error("[searchFilesEpic] Error:", err);
            console.error(err.message || "Error searching files");
            return of(
              fileActions.searchFilesFailure(
                err.message || "Error searching files"
              )
            );
          })
        ),
        [stopLoading({ key: "searchFiles" })]
      );
    })
  );

export const getFileVersionsEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(fileActions.getFileVersionsRequest.match),
    switchMap((action) => {
      const fileId = action.payload;
      return concat(
        from(filesService.getFileVersions(fileId).toPromise()).pipe(
          map((versions) => {
            if (!versions) {
              return fileActions.getFileVersionsFailure("No versions returned");
            }
            return fileActions.getFileVersionsSuccess(versions);
          }),
          catchError((err) => {
            console.error("[getFileVersionsEpic] Error:", err);
            console.error(err.message || "Error fetching file versions");
            return of(
              fileActions.getFileVersionsFailure(
                err.message || "Error fetching file versions"
              )
            );
          })
        ),
      );
    })
  );

export const setDocumentActiveEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(fileActions.setDocumentActiveRequest.match),
    switchMap((action) => {
      const { newDocumentId, oldDocumentId, fileId } = action.payload;

      // Tạo observable để deactivate old (nếu có)
      const deactivateOld$ = oldDocumentId
        ? from(filesService.setDocumentActive(oldDocumentId, false).toPromise())
        : of(null);

      // Tạo observable để activate new
      const activateNew$ = from(
        filesService.setDocumentActive(newDocumentId, true).toPromise()
      );

      return concat(
        [startLoading({ key: "setDocumentActive" })],
        // Thực hiện tuần tự: deactivate old -> activate new
        deactivateOld$.pipe(
          concatMap(() => {
            console.log(
              "✅ Old version deactivated, now activating new version..."
            );
            return activateNew$;
          }),
          concatMap((response) => {
            // API đã trả về response, giờ mới refetch
            console.log("✅ API set active completed:", response);
            console.log("📍 Current time:", new Date().toISOString());
            const successMsg =
              i18next?.t?.("files:version_updated_success") ||
              "Version updated successfully";
            message.success(successMsg);

            // Dispatch success action
            const successAction = fileActions.setDocumentActiveSuccess({
              newDocumentId,
              fileId,
            });

            // Refetch file versions để update modal
            const refetchVersionAction =
              fileActions.getFileVersionsRequest(fileId);

            // Refetch files list để update trang Files
            // Lấy folderId từ localStorage
            const folderId = localStorage.getItem("folderId");


            if (folderId) {
              const updateLastUploadAtAction =
                uploadFileActions.setLastUploadAt(Date.now());

              return from([
                successAction,
                refetchVersionAction,
                updateLastUploadAtAction,
              ]);
            } else {
              console.log("⚠️ No folderId found in localStorage");
              return from([successAction, refetchVersionAction]);
            }
          }),
          catchError((err) => {
            console.error("[setDocumentActiveEpic] Error:", err);
            console.error(err.message || "Failed to update version");
            return of(
              fileActions.setDocumentActiveFailure(
                err.message || "Failed to update version"
              )
            );
          })
        ),
        [stopLoading({ key: "setDocumentActive" })]
      );
    })
  );

export const getUserStorageInfoEpic$: RootEpic = (action$) =>
  action$.pipe(
    filter(fileActions.getUserStorageInfoRequest.match),
    switchMap(() => {
      const userProfile = getUserProfile();
      const companyId = localStorage.getItem("companyId") || "";

      if (!userProfile?.Id) {
        return of(fileActions.getUserStorageInfoFailure("User ID not found"));
      }

      return filesService.getUserStorageInfo(userProfile.Id, companyId).pipe(
        map((response) => fileActions.getUserStorageInfoSuccess(response)),
        catchError((err) => {
          console.error("Error fetching user storage info:", err);
          return of(
            fileActions.getUserStorageInfoFailure(
              err.message || "Error loading user storage info"
            )
          );
        })
      );
    })
  );

export const fileEpics = [
  SearchFilesEpic$,
  getFilesEpic$,
  getAllFileContent$,
  createFileEpic$,
  deleteFileEpic$,
  deleteFileGroupEpic$,
  backToPreviousFolderEpic$,
  getDocumentByIdRequest$,
  downloadFile$,
  fetchIraiId$,
  filterFilesEpic$,
  getFileVersionsEpic$,
  setDocumentActiveEpic$,
  getUserStorageInfoEpic$,
  fetchThumbnailsEpic$,
  // New epic-based file fetching
  fetchFilesByFolderIdEpic,
  fetchStorageForLabelsEpic,
];

export const fileActions = fileSlice.actions;

export const fileReducer = fileSlice.reducer;

// Selectors
export const selectParentFiles = (state: any) => state.file.parentFiles;

export const selectParentFilesWithStatus = createSelector(
  (state: any) => state.file.parentFiles,
  (state: any) => {
    return state.fileStatus ? selectMergedFileStatus(state) : [];
  },
  (parentFiles: any[], mergedStatus: any[]) => {
    if (!parentFiles?.length) return [];

    return parentFiles.map((f: any) => {
      const fileId = String(f.id ?? "");
      const status = mergedStatus.find(
        (m: any) => String(m.fileId) === String(fileId)
      );
      return {
        ...f,
        convertStatus: status?.convertStatus ?? null,
        extractStatus: status?.extractStatus ?? null,
        statusFinal: status?.statusFinal ?? 1,
      };
    });
  }
);

export const setDataSearch = createSelector(
  (state: any) => state.file.searchFiles.results,
  (state: any) => {
    return state.fileStatus ? selectMergedFileStatus(state) : [];
  },
  (searchFiles: any[], mergedStatus: any[]) => {
    if (!searchFiles?.length) return [];

    return searchFiles.map((f: any) => {
      const fileId = String(f.id ?? "");
      const status = mergedStatus.find(
        (m: any) => String(m.fileId) === String(fileId)
      );
      return {
        ...f,
        convertStatus: status?.convertStatus ?? null,
        extractStatus: status?.extractStatus ?? null,
        statusFinal: status?.statusFinal ?? 1,
      };
    });
  }
);

// Selectors for user storage
export const selectUserStorage = (state: any): number =>
  state?.file?.userStorage?.storageSize ?? 0;
export const selectUserStorageLoading = (state: any) =>
  state.file.userStorageLoading;
export const selectThumbnailsById = (state: any) => state.file.thumbnailsById;
export const selectThumbnailsLoading = (state: any) => state.file.thumbnailsLoading;
